// Rapport d'intervention : vue HTML (mêmes données que le PDF) + téléchargement du PDF.
import type { ReactNode } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Download, FlaskConical } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { Alerte, Badge, Chargement, EntetePage, EtatErreur, FilContexte, LienBouton, Panneau } from '../../components/ui.tsx';
import { BadgeStatutIntervention } from '../../components/statuts.tsx';
import { SourceExtrait } from './composants.tsx';

interface SourceRapport { titre: string; version: string | null; page: number | null; version_id: string | null }
interface LigneRapport { texte: string; detail?: string | null; statut?: string | null; source?: SourceRapport | null; ia?: boolean }
interface Rapport {
  reference: string; titre: string; statut: string; statut_libelle: string; demo: boolean;
  site: { id: string; nom: string; client: string | null; adresse: string | null };
  equipement: { libelle: string; fabricant: string | null; gamme: string | null; reference: string | null; versions: string | null; emplacement: string | null } | null;
  dates: { creation: string; modification: string; cloture: string | null };
  intervenant: string | null; symptome: string | null; contexte: { libelle: string; valeur: string }[]; securite: string[];
  mesures: { point: string; valeur: string; unite: string; source?: SourceRapport | null }[];
  observations: LigneRapport[]; controles: LigneRapport[]; hypotheses_ecartees: LigneRapport[]; causes: LigneRapport[]; operations: LigneRapport[];
  suites: { resolution: string | null; escalade: string | null; escalades: LigneRapport[]; restant: LigneRapport[]; non_realises: LigneRapport[]; notes: LigneRapport[] };
  sources: SourceRapport[]; contient_ia: boolean; genere_le: string;
}

const NR = <span className="inconnu">Non renseigné</span>;

function Champ({ l, v, mono }: { l: string; v: ReactNode; mono?: boolean }) {
  return <div><dt>{l}</dt><dd className={mono ? 'mono' : undefined}>{v || NR}</dd></div>;
}

function Lignes({ lignes, prefixe }: { lignes: LigneRapport[]; prefixe?: string }) {
  if (!lignes.length) return <p>{NR}</p>;
  return (
    <ul className="int-rapport-liste">
      {lignes.map((l, k) => (
        <li key={k}>
          <span>{l.texte}</span>{l.ia && <> <Badge ton="info" icone={false}>Proposé par l’assistance</Badge></>}
          {l.detail && <p className="petit secondaire" style={{ whiteSpace: 'pre-wrap' }}>{prefixe}{l.detail}</p>}
          {l.source && <div><SourceExtrait versionId={l.source.version_id} page={l.source.page} titre={l.source.titre} version={l.source.version} /></div>}
        </li>
      ))}
    </ul>
  );
}

export function PageRapport() {
  const { id } = useParams();
  const q = useQuery({ queryKey: ['intervention-rapport', id], queryFn: () => api.get<Rapport>(`/api/interventions/${id}/rapport`) });
  if (q.isLoading) return <Chargement lignes={6} />;
  if (q.isError || !q.data) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const r = q.data;
  return (
    <div className="pile-l int-rapport">
      <EntetePage
        fil={<FilContexte noeuds={[{ type: 'Site', libelle: r.site.nom, to: `/sites/${r.site.id}` },
          { type: 'Intervention', libelle: r.reference, to: `/interventions/${id}` }, { type: 'Rapport', libelle: 'Rapport' }]} />}
        titre={<span className="rangee" style={{ gap: 10 }}>Rapport {r.reference}{r.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}</span>}
        sousTitre={<span className="rangee" style={{ gap: 8 }}>{r.titre}<BadgeStatutIntervention statut={r.statut} /></span>}
        actions={<>
          <a className="btn btn-principal" href={`/api/interventions/${id}/rapport.pdf`} download><Download aria-hidden />Télécharger le PDF</a>
          <LienBouton to={`/interventions/${id}`}>Retour au diagnostic</LienBouton>
        </>} />
      {r.demo && <Alerte ton="ambre" titre="Démonstration — données fictives" />}
      {r.securite.map((s, k) => <Alerte key={k} ton="ambre" titre="Sécurité">{s}</Alerte>)}

      <Panneau titre="Identification">
        <dl className="fiche">
          <Champ l="Site" v={[r.site.nom, r.site.client && `client ${r.site.client}`].filter(Boolean).join(' — ')} />
          <Champ l="Adresse" v={r.site.adresse} />
          <Champ l="Équipement" v={r.equipement?.libelle} />
          <Champ l="Emplacement" v={r.equipement?.emplacement} />
          <Champ l="Fabricant / gamme" v={r.equipement && [r.equipement.fabricant, r.equipement.gamme].filter(Boolean).join(' — ')} />
          <Champ l="Référence" v={r.equipement?.reference} mono />
          <Champ l="Versions" v={r.equipement?.versions} />
          <Champ l="Intervenant" v={r.intervenant} />
          <Champ l="Ouverte le" v={r.dates.creation} />
          <Champ l="Dernière modification" v={r.dates.modification} />
          {r.dates.cloture && <Champ l="Clôturée le" v={r.dates.cloture} />}
        </dl>
      </Panneau>

      <Panneau titre="Symptôme et contexte">
        <div className="pile">
          <p style={{ whiteSpace: 'pre-wrap' }}>{r.symptome ?? NR}</p>
          {r.contexte.length > 0 && <dl className="fiche">{r.contexte.map((c) => <Champ key={c.libelle} l={c.libelle} v={c.valeur} />)}</dl>}
        </div>
      </Panneau>

      <div className="grille-2">
        <Panneau titre="Mesures">
          {r.mesures.length ? (
            <ul className="int-rapport-liste">{r.mesures.map((m, k) => <li key={k}>{m.point} : <strong className="mono chiffre">{m.valeur} {m.unite}</strong></li>)}</ul>
          ) : <p>{NR}</p>}
        </Panneau>
        <Panneau titre="Observations"><Lignes lignes={r.observations} /></Panneau>
      </div>
      <Panneau titre="Contrôles réalisés et résultats"><Lignes lignes={r.controles} prefixe="Résultat : " /></Panneau>
      <div className="grille-2">
        <Panneau titre="Hypothèses écartées"><Lignes lignes={r.hypotheses_ecartees} /></Panneau>
        <Panneau titre="Cause confirmée"><Lignes lignes={r.causes} /></Panneau>
      </div>
      <Panneau titre="Opérations réalisées"><Lignes lignes={r.operations} /></Panneau>
      <Panneau titre="Suites à donner">
        <div className="pile">
          <dl className="fiche"><Champ l="Résolution" v={r.suites.resolution} /><Champ l="Escalade" v={r.suites.escalade} /></dl>
          {r.suites.escalades.length > 0 && <Lignes lignes={r.suites.escalades} />}
          {r.suites.restant.length > 0 && <><h3 className="petit">Contrôles et opérations restant à faire</h3><Lignes lignes={r.suites.restant} /></>}
          {r.suites.non_realises.length > 0 && <><h3 className="petit">Contrôles proposés non réalisés</h3><Lignes lignes={r.suites.non_realises} /></>}
          {r.suites.notes.length > 0 && <><h3 className="petit">Notes</h3><Lignes lignes={r.suites.notes} /></>}
        </div>
      </Panneau>
      <Panneau titre="Sources documentaires citées">
        {r.sources.length ? (
          <ul className="int-rapport-liste">{r.sources.map((s, k) => <li key={k}><SourceExtrait versionId={s.version_id} page={s.page} titre={s.titre} version={s.version} /></li>)}</ul>
        ) : <p>{NR}</p>}
      </Panneau>
      <p className="tres-petit discret">
        Rapport généré le {r.genere_le}.{r.demo ? ' Démonstration — données fictives.' : ''}
        {r.contient_ia ? ' Éléments proposés par un système d’IA, vérifiés par l’intervenant.' : ''}
      </p>
    </div>
  );
}
